import { elementEvents } from 'library/ts/ui/events/ElementEvent';
import { LogService } from 'library/ts/utilities/logger/LogService';
import * as Type from 'library/ts/utilities/modules/Type';

const logger = LogService.create( { prefixes: [ 'SmartTag:' ] } );

/**
 * @description Checks if browser supports 'passive' property
 * @see {@link https://www.chromestatus.com/feature/5745543795965952}
 * @see {@link https://github.com/WICG/EventListenerOptions/blob/gh-pages/explainer.md}
 * @return {Boolean}
 */
export const supportsPassive = (): boolean => {
	// Test via a getter in the options object to see if the passive property is accessed
	let supportsPassive: boolean = false;
	try {
		const opts = Object.defineProperty( {}, 'passive', {
			// eslint-disable-next-line getter-return
			get: () => {
				supportsPassive = true;
			}
		} );
		window.addEventListener( 'testPassive', null, opts );
		window.removeEventListener( 'testPassive', null, opts );
	} catch ( e ) {
		// error
	}
	return supportsPassive;
};

/**
 * @description Add once event listener
 * @see {@link http://stackoverflow.com/questions/3393686/only-fire-an-event-once}
 * @param target {Object}
 * @param type {String}
 * @param listener {( event: MouseEvent | TouchEvent | any ) => void}
 */
export const addListenerOnce = ( target: any, type: string, listener: ( event: MouseEvent | TouchEvent | any ) => void ): void => {
	target.addEventListener( type, function fn( event ) {
		target.removeEventListener( type, fn );
		listener( event );
	} );
};

/**
 * @description Add  element events
 * @param {HTMLElement } element
 * @param {(event: MouseEvent | TouchEvent) => void} eventHandler
 */
export const addElementEvents = ( element: HTMLElement, eventHandler: ( event: MouseEvent | TouchEvent ) => void ): void => {
	// add overlay event listener...
	elementEvents().forEach(
		( event ) => {
			if ( Type.isElement( element ) && Type.isFunction( eventHandler ) ) {
				element.addEventListener( event, eventHandler, ( supportsPassive() ? { passive: true } : false ) );
			}
		}
	);
};

/**
 * @description Remove  element events
 * @param {HTMLElement } element
 * @param {(event: MouseEvent | TouchEvent | any ) => void} eventHandler
 */
export const removeElementEvents = ( element: HTMLElement, eventHandler: ( event: MouseEvent | TouchEvent ) => void ): void => {
	// add overlay event listener...
	elementEvents().forEach(
		( event ) => {
			if ( Type.isElement( element ) && Type.isFunction( eventHandler ) ) {
				element.removeEventListener( event, eventHandler );
			}
		}
	);
};

/**
 * @description Console table output of all DOM event listeners
 * @see {@link https://github.com/alex2844/js-events}
 * @param {any[]} baseNodeList
 * @param {NodeListOf<Element | HTMLElement>} nodeList
 * @param {string} app
 * @example
 * ```typescript
 * // Output DOM event listeners for application
 * app_namespace.utilities.Event.getListeners( [], document.querySelector( '[app-css-class-selector]' ).querySelectorAll( '*' ) )
 * ```
 */
export const getListeners = (
	baseNodeList: ( HTMLElement | Element | any )[] = [ document, window ],
	nodeList: NodeListOf<Element | HTMLElement> = document.querySelectorAll( '*' ),
	app: string = ''
): void => {
	// check if API exists on window level
	if ( ( <any>window ).getEventListeners === undefined ) {
		logger.warn( app, 'Utilities:', 'getListeners:', 'window.getEventListeners does not exist on window object' );
		return;
	}

	logger.table(
		baseNodeList.concat( [ ...nodeList ] ).map( ( element ) => {
			const evs = ( <any>window ).getEventListeners( element );
			// evs = (evs.click ? { click: evs.click } : []);
			return {
				element: element,
				types: Object.keys( evs ).join( ', ' ),
				listeners: evs
			};
		} ).filter( ( item ) => item.types )
	);

};